import React, { useEffect, useState } from 'react';
import { Button, Box, Container, Paper, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import * as invoiceService from '../services/invoiceService';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import jsPDF from 'jspdf';
import { fillInvoiceTemplate } from '../components/invoiceTemplateUtils';

const ListInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await invoiceService.getInvoices();
      
      const invoicesArray = Array.isArray(data?.payload)
        ? data.payload
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      
      setInvoices(invoicesArray);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewInvoicePdf = async (invoice) => {
    const templateHtml = await fetch('/invoicetemplates/template-fr.html').then((res) => res.text());
    const filledTemplate = fillInvoiceTemplate(templateHtml, invoice);

    const doc = new jsPDF({
      format: 'a4',
      unit: 'px',
      orientation: 'portrait',
    });
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;

    doc.html(tempDiv, {
      callback: function (doc) {
        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
      },
      x: 20,
      y: 20,
      width: 380,
      windowWidth: 380,
    });
  };

  const columns = [
    { field: 'Id', headerName: 'ID', flex: 0.5, minWidth: 80 },
    {
      field: 'CustomerId',
      headerName: 'Customer',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (params.row.CustomerName ? params.row.CustomerName : '-'),
    },
    { field: 'InvoiceNumber', headerName: 'Invoice Number', flex: 1, minWidth: 150 },
    { 
      field: 'InvoicePaidAmount', 
      headerName: 'Paid Amount', 
      flex: 1, 
      align: 'center',
      minWidth: 120,
      headerAlign: 'center',
      renderCell: (params) => {
        const paidAmount = Number(params.row.InvoicePaidAmount || 0);
        return (
          <Typography variant="body2" fontWeight={500}>
            €{paidAmount.toFixed(2)}
          </Typography>
        );
      }
    },
    {
      field: 'ProductCount',
      headerName: 'Items',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.LineItems ? params.row.LineItems.length : 0),
    },
    {
      field: 'IsSendOnPeppol',
      headerName: 'Send on Peppol',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        return (
          <Chip
            icon={isSendOnPeppol ? <CheckCircleIcon /> : <CancelIcon />}
            label={isSendOnPeppol ? 'Yes' : 'No'}
            color={isSendOnPeppol ? 'success' : 'default'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              minWidth: 70,
            }}
          />
        );
      },
    },
    {
      field: 'PeppolStatus',
      headerName: 'Peppol Status',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        const peppolStatus = params.row.PeppolStatus || 'Not Sent';
        
        let icon;
        let color;
        let label;

        if (!isSendOnPeppol) {
          icon = <CancelIcon />;
          color = 'default';
          label = 'N/A';
        } else {
          switch (peppolStatus.toLowerCase()) {
            case 'sent':
            case 'delivered':
            case 'success':
              icon = <CheckCircleIcon />;
              color = 'success';
              label = 'Sent';
              break;
            case 'pending':
            case 'processing':
              icon = <PendingIcon />;
              color = 'warning';
              label = 'Pending';
              break;
            case 'failed':
            case 'error':
              icon = <CancelIcon />;
              color = 'error';
              label = 'Failed';
              break;
            default:
              icon = <PendingIcon />;
              color = 'info';
              label = 'Not Sent';
          }
        }

        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              minWidth: 85,
            }}
          />
        );
      },
    },
    {
      field: 'InvoiceUrl',
      headerName: 'PDF',
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row ? (
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s'
              }
            }}
            title="Preview PDF"
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewInvoicePdf(params.row);
            }}
          >
            <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
          </Box>
        ) : null,
    },
    {
      field: 'Status',
      headerName: 'Payment Status',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const paid = Number(params.row.InvoicePaidAmount) || 0;
        const total = Number(params.row.InvoicePrice) || 0;
        let label = '';
        let bg = '';
        if (paid === 0) {
          label = 'Not Paid';
          bg = '#d32f2f';
        } else if (paid >= total) {
          label = 'Paid';
          bg = '#388e3c';
        } else {
          label = 'Partial';
          bg = '#fbc02d';
        }
        return (
          <span
            style={{
              color: '#fff',
              background: bg,
              borderRadius: 10,
              padding: '1px 8px',
              fontWeight: 500,
              fontSize: 11,
              display: 'inline-block',
              minWidth: 60,
              lineHeight: 1.6,
              textAlign: 'center',
              letterSpacing: 0.2,
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button 
          size="small"
          variant="outlined"
          onClick={() => navigate(`/invoices/view/${params.row.Id}`)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your invoices
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/invoices/add')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.2s',
              },
            }}
          >
            Add Invoice
          </Button>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReusableDataGrid
          rows={invoices}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          checkboxSelection
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          }}
        />
      </Paper>
    </Container>
  );
};

export default ListInvoices;